import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowLeft,
  CalendarDays,
  Clock,
  Share2,
  Check,
  Tag,
  ArrowRight,
  Wifi,
  Sparkles,
  BookOpen,
} from "lucide-react";
import { siteContentQuery, useSiteSection } from "@/hooks/useSiteContent";
import { siteImageUrl } from "@/hooks/useSiteData";
import { findPost, plainText, postParagraphs, postSlug, estimateReadingTime } from "@/lib/blog";
import { toast } from "sonner";

export const Route = createFileRoute("/blog/$slug")({
  head: ({ params }) => {
    const title = params.slug.replace(/-/g, " ");
    return {
      meta: [
        { title: `${title} — Blog Contato` },
        { name: "description", content: `Leia o artigo "${title}" no blog da Contato Internet.` },
        { property: "og:title", content: `${title} — Blog Contato` },
        { property: "og:description", content: `Confira este artigo completo no Blog da Contato.` },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: PostPage,
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <div className="mx-auto flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
        <BookOpen className="size-8" />
      </div>
      <h1 className="mt-4 text-2xl font-extrabold text-brand-deep">Artigo não encontrado</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        O artigo que você procura foi movido ou não existe mais.
      </p>
      <Link
        to="/blog"
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground shadow transition hover:opacity-90"
      >
        <ArrowLeft className="size-4" /> Voltar para o blog
      </Link>
    </div>
  ),
  errorComponent: () => (
    <div className="mx-auto max-w-2xl px-5 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-brand-deep">Não foi possível carregar o artigo</h1>
      <Link to="/blog" className="mt-4 inline-block font-extrabold text-primary hover:underline">
        Voltar para o blog
      </Link>
    </div>
  ),
});

function PostPage() {
  const { slug } = Route.useParams();
  const blog = useSiteSection("blog");
  const { isPending } = useQuery(siteContentQuery);
  const posts = (blog.items ?? []).filter((p) => p.title && p.title.trim() !== "");
  const post = findPost(posts, slug);

  const [copied, setCopied] = useState(false);

  // Link aberto direto: enquanto o conteúdo do banco não chegou, só há os posts padrão.
  if (!post && isPending) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center text-sm text-muted-foreground">
        Carregando artigo…
      </div>
    );
  }
  if (!post) throw notFound();

  const image = siteImageUrl(post.image);
  const readTime = post.readTime || estimateReadingTime(post.body || post.excerpt);
  const isHtml = post.body && /<\/?[a-z][\s\S]*>/i.test(post.body);
  const paragraphs = postParagraphs(post);

  // Posts relacionados (mesma categoria ou outros posts)
  const relatedPosts = posts
    .filter((p) => postSlug(p) !== slug)
    .sort((a, b) => {
      if (post.category && a.category === post.category && b.category !== post.category) return -1;
      if (post.category && b.category === post.category && a.category !== post.category) return 1;
      return 0;
    })
    .slice(0, 3);

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: plainText(post.excerpt),
          url,
        });
      } catch {
        // Usuário cancelou
      }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiado para a área de transferência!");
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const shareWhatsApp = () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const text = encodeURIComponent(`*${post.title}*\n${plainText(post.excerpt)}\n\nLeia mais: ${url}`);
    window.open(`https://api.whatsapp.com/send?text=${text}`, "_blank");
  };

  return (
    <>
      {/* Header do Artigo */}
      <section className="relative overflow-hidden bg-[linear-gradient(115deg,var(--brand-sky-deep),var(--brand-deep))] py-12 text-brand-deep-foreground sm:py-16">
        <div className="relative mx-auto max-w-4xl px-5">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-brand-yellow backdrop-blur-sm transition hover:bg-white/20"
          >
            <ArrowLeft className="size-3.5" /> Voltar ao Blog
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-bold text-white/80">
            {post.category && (
              <span className="rounded-full bg-white/20 px-3 py-1 text-white">
                <Tag className="mr-1 inline size-3" />
                {post.category}
              </span>
            )}
            {post.date && (
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="size-3.5" />
                {post.date}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-3.5" />
              {readTime}
            </span>
            {post.author && (
              <span className="inline-flex items-center gap-1 text-brand-sky">
                • Por {post.author}
              </span>
            )}
          </div>

          <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/90 sm:text-xl">
              {plainText(post.excerpt)}
            </p>
          )}
        </div>
      </section>

      {/* Corpo do Artigo */}
      <article className="bg-background py-10 sm:py-16">
        <div className="mx-auto max-w-4xl px-5">
          {/* Imagem de Capa */}
          {image && (
            <div className="mb-10 overflow-hidden rounded-3xl border border-border shadow-md">
              <img
                src={image}
                alt={post.title}
                className="aspect-[16/9] w-full object-cover"
              />
            </div>
          )}

          {/* Barra de Ações Rápidas / Compartilhamento */}
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-y border-border py-4">
            <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-muted-foreground">
              <Share2 className="size-4" /> Compartilhar artigo:
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={shareWhatsApp}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3.5 py-1.5 text-xs font-bold text-white shadow-sm transition hover:opacity-90"
              >
                WhatsApp
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-xs font-bold text-foreground transition hover:bg-secondary"
              >
                {copied ? (
                  <>
                    <Check className="size-3.5 text-green-500" /> Copiado
                  </>
                ) : (
                  <>Copiar link</>
                )}
              </button>
            </div>
          </div>

          {/* Conteúdo Renderizado */}
          <div className="prose prose-lg dark:prose-invert max-w-none text-foreground/90">
            {isHtml ? (
              <div
                className="space-y-4 leading-relaxed [&_h2]:mt-8 [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:text-brand-deep [&_h3]:mt-6 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-brand-deep [&_p]:text-base [&_p]:leading-relaxed sm:[&_p]:text-lg [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:space-y-2 [&_ol]:ml-6 [&_ol]:list-decimal [&_ol]:space-y-2 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_a]:font-bold [&_a]:text-primary [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: post.body! }}
              />
            ) : (
              <div className="space-y-5 text-base leading-relaxed sm:text-lg">
                {paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            )}
          </div>

          {/* Banner de Conversão / CTA da Contato no final do artigo */}
          <div className="mt-14 overflow-hidden rounded-3xl bg-[linear-gradient(115deg,var(--brand-sky-deep),var(--brand-deep))] p-8 text-brand-deep-foreground shadow-lg sm:p-10">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
              <div>
                <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs font-extrabold uppercase tracking-widest text-brand-yellow">
                  <Wifi className="size-3.5" /> Internet Fibra de Alta Velocidade
                </span>
                <h3 className="mt-3 text-2xl font-extrabold text-white sm:text-3xl">
                  Quer a melhor conexão para a sua casa ou empresa?
                </h3>
                <p className="mt-2 max-w-xl text-sm text-white/80 sm:text-base">
                  Navegue com ultravelocidade, estabilidade de fibra óptica e o melhor suporte do Sul de SC.
                </p>
              </div>
              <Link
                to="/planos"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-yellow px-6 py-3.5 text-sm font-black uppercase tracking-wide text-brand-deep shadow-xl transition hover:brightness-105"
              >
                Conhecer Planos
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>

          {/* Artigos Relacionados */}
          {relatedPosts.length > 0 && (
            <div className="mt-16 border-t border-border pt-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-extrabold uppercase tracking-wide text-brand-deep sm:text-2xl">
                  Leia também
                </h2>
                <Link
                  to="/blog"
                  className="text-xs font-extrabold uppercase tracking-wider text-primary hover:underline"
                >
                  Ver todos os artigos →
                </Link>
              </div>

              <div className="mt-6 grid gap-6 sm:grid-cols-3">
                {relatedPosts.map((p) => {
                  const relImg = siteImageUrl(p.image);
                  return (
                    <Link
                      key={postSlug(p)}
                      to="/blog/$slug"
                      params={{ slug: postSlug(p) }}
                      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-1 hover:shadow-md"
                    >
                      {relImg ? (
                        <img
                          src={relImg}
                          alt={p.title}
                          className="aspect-[16/10] w-full rounded-xl object-cover"
                        />
                      ) : (
                        <div className="flex aspect-[16/10] w-full items-center justify-center rounded-xl bg-muted text-primary">
                          <BookOpen className="size-8" />
                        </div>
                      )}
                      {p.category && (
                        <span className="mt-3 text-[11px] font-bold text-primary">
                          {p.category}
                        </span>
                      )}
                      <h4 className="mt-1 text-sm font-bold text-foreground transition group-hover:text-primary">
                        {p.title}
                      </h4>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}
